import { MessageCircle } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { MESSAGES_CARD_TITLE, OPEN_FULL_THREAD_LABEL, threadPath } from './constants';
import { ThreadPanel } from './thread-panel';

/**
 * The connection detail's **Messages card** — `Web App.dc.html` §2.11's left column, top, which
 * is the slot `connection-detail-page.tsx` has been holding open since Phase 4.
 *
 * `MEM-054` AC-2: *"connection detail carries a Messages card"*. The prototype draws a header with
 * a `message-circle` glyph, a body of bubbles on `--surface-page-alt`, an empty state, and a
 * composer with a labelled Send — and every one of those is `ThreadPanel`'s already, so this file
 * is the card around it and the one thing the card adds.
 *
 * **The header is drawn here and not by the panel.** The page above already names the person, at
 * `text-4xl`, with their role and their state pill; a second header inside the card would be that
 * name twice on one screen. `headerless` is what says so.
 *
 * ## The link out, which the prototype does not have
 *
 * *Open the full conversation* goes to `/app/messages/{ref}`. The prototype's card is the whole
 * thread, unbounded, inside a column that also holds the note — which is fine for five seeded
 * messages and wrong for a year of them. The card shows the conversation in place and the link
 * opens the thread where it has the screen to itself; it is also the only way, from this page, to
 * reach the address a notification lands on.
 */
export function ConnectionMessagesPanel({
  connectionRef,
}: {
  readonly connectionRef: string;
}): React.ReactElement {
  return (
    <section className="flex flex-col overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm">
      <header className="flex items-center gap-2 border-b border-border-subtle px-[22px] py-[18px]">
        <MessageCircle className="h-[18px] w-[18px] text-primary" aria-hidden />
        <h2 className="flex-1 text-lg">{MESSAGES_CARD_TITLE}</h2>
        <Link
          to={threadPath(connectionRef)}
          className="font-brand text-xs font-bold text-primary hover:underline"
        >
          {OPEN_FULL_THREAD_LABEL}
        </Link>
      </header>
      <div className="flex max-h-[420px] min-h-[220px] flex-col">
        <ThreadPanel connectionRef={connectionRef} variant="card" headerless />
      </div>
    </section>
  );
}
